/**
 * Classification picker — a dropdown of the standard construction classes +
 * every distinct classification already on the roster + the current value,
 * with a "Custom…" option that flips to a free-text input for a genuinely new
 * class (which then auto-appears in future dropdowns once the employee saves).
 *
 * Closed-by-default (dropdown) to stop typo drift ("Operator" vs "operator");
 * the Custom escape hatch is user-initiated. Self-fetches the roster via the
 * shared useSettingsEmployees hook (same source as InlineClassSelect), so
 * callers just drop it in. The closed-state control is the boxed `Dropdown`
 * primitive and fills the field's width on its own; `className` only reaches
 * the Custom-mode text input, matching the surrounding form's input styling.
 */
import { useMemo, useState } from 'react';
import { useSettingsEmployees } from '../lib/useSettings';
import { Dropdown, type DropdownOption } from './design';

const STANDARD_CLASSIFICATIONS = ['Foreman', 'Operator', 'Journeyman', 'Apprentice', 'Laborer'];
const CUSTOM = '__custom__';

export interface ClassificationSelectProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ClassificationSelect({ value, onChange, disabled, className }: ClassificationSelectProps) {
  const employees = useSettingsEmployees();
  const [custom, setCustom] = useState(false);

  const options = useMemo<DropdownOption[]>(() => {
    const seen = new Set<string>();
    const out: DropdownOption[] = [{ value: '', label: 'Select classification…' }];
    const add = (v: string) => {
      const t = v.trim();
      if (t !== '' && !seen.has(t)) {
        seen.add(t);
        out.push({ value: t, label: t });
      }
    };
    STANDARD_CLASSIFICATIONS.forEach(add);
    (employees.data ?? []).forEach((e) => add(e.classification));
    add(value);
    out.push({ value: CUSTOM, label: 'Custom…' });
    return out;
  }, [employees.data, value]);

  if (custom) {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="New classification"
          autoFocus
        />
        <button
          type="button"
          className="btn-quiet"
          style={{ alignSelf: 'flex-start', fontSize: 11 }}
          onClick={() => { setCustom(false); onChange(''); }}
          disabled={disabled}
        >
          ↩ Pick from list
        </button>
      </span>
    );
  }

  return (
    <Dropdown
      style={{ width: '100%' }}
      options={options}
      value={value}
      onChange={(v) => {
        if (v === CUSTOM) {
          setCustom(true);
          onChange('');
        } else {
          onChange(v);
        }
      }}
      disabled={disabled}
    />
  );
}
